import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  Link, 
  Image, 
  Video, 
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Quote,
  Code,
  Upload,
  X,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Table
} from 'lucide-react';

const EditorContainer = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 4px;
  background: ${props => props.active ? '#ff6b35' : 'transparent'};
  color: ${props => props.active ? 'white' : '#cccccc'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#ff8c5a' : 'rgba(255, 255, 255, 0.1)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToolbarSeparator = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.2);
  margin: 0 4px;
`;

const EditorContent = styled.div`
  min-height: 300px;
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #cccccc;
  outline: none;
  
  /* Preserve br tags and prevent automatic div creation */
  br {
    display: block;
    content: "";
    margin: 0.5em 0;
  }
  
  /* Prevent empty divs from collapsing */
  div:empty {
    min-height: 1.6em;
    margin: 0.5em 0;
  }

  &:empty:before {
    content: 'Start writing your article...';
    color: #888888;
    font-style: italic;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 16px 0;
    display: block;
  }

  iframe {
    max-width: 100%;
    border-radius: 8px;
    margin: 16px 0;
  }

  blockquote {
    border-left: 4px solid #ff6b35;
    padding-left: 16px;
    margin: 16px 0;
    font-style: italic;
    color: #cccccc;
    background: rgba(255, 107, 53, 0.1);
    padding: 16px;
    border-radius: 8px;
  }

  pre {
    background: rgba(0, 0, 0, 0.3);
    color: #cccccc;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
  }

  code {
    background: rgba(255, 255, 255, 0.1);
    color: #ff6b35;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  ul, ol {
    padding-left: 24px;
    margin: 16px 0;
  }

  li {
    margin: 8px 0;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 24px 0 16px 0;
    font-weight: 600;
    line-height: 1.3;
  }

  h1 { font-size: 2rem; }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.5rem; }
  h4 { font-size: 1.25rem; }
  h5 { font-size: 1.1rem; }
  h6 { font-size: 1rem; }

  p {
    margin: 16px 0;
  }

  a {
    color: #ff6b35;
    text-decoration: underline;
    
    &:hover {
      color: #ff8c5a;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffffff;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #cccccc;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #cccccc;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }

  &::placeholder {
    color: #888888;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }

  &::placeholder {
    color: #888888;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c5a 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
  }

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ButtonSecondary = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-right: 12px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 255, 255, 0.1);
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: #ff6b35;
  }

  &.dragover {
    border-color: #ff6b35;
    background: rgba(255, 107, 53, 0.1);
  }
`;

const UploadText = styled.div`
  color: #cccccc;
  font-size: 14px;
  margin-bottom: 8px;
`;

const UploadSubtext = styled.div`
  color: #888888;
  font-size: 12px;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  margin: 16px 0;
`;

const RichTextEditor = ({ value, onChange, placeholder = "Start writing your article..." }) => {
  const editorRef = useRef(null);
  const isTypingRef = useRef(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [imageData, setImageData] = useState({ url: '', alt: '' });
  const [videoData, setVideoData] = useState({ url: '', title: '' });
  const [tableData, setTableData] = useState({ rows: 3, cols: 3 });
  const [uploading, setUploading] = useState(false);

  // Initialize editor to use br tags instead of div tags
  useEffect(() => {
    if (editorRef.current) {
      // Set default paragraph separator to 'br' to prevent div creation
      if (document.execCommand) {
        try {
          document.execCommand('defaultParagraphSeparator', false, 'br');
        } catch (e) {
          // Some browsers don't support this, that's okay
        }
      }
    }
  }, []);

  // Normalize HTML content: convert empty div/p tags to br tags for better line break preservation
  const normalizeContent = (html) => {
    if (!html) return html;
    
    // If content is just text or already has br tags, return as-is
    if (!html.includes('<div') && !html.includes('<p>') && !html.includes('<p ')) {
      return html;
    }
    
    // Create a temporary div to parse and normalize the HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Replace empty div and p tags with br tags
    const emptyBlocks = temp.querySelectorAll('div:empty, p:empty');
    emptyBlocks.forEach(block => {
      const br = document.createElement('br');
      if (block.parentNode) {
        block.parentNode.replaceChild(br, block);
      }
    });
    
    // Also handle div/p tags that only contain whitespace or single br
    // But be careful not to replace divs/p that contain actual content
    const whitespaceBlocks = temp.querySelectorAll('div, p');
    whitespaceBlocks.forEach(block => {
      const text = block.textContent?.trim() || '';
      const innerHTML = block.innerHTML.trim();
      const hasOnlyBr = innerHTML === '<br>' || innerHTML === '<br/>' || innerHTML === '';
      // Only replace if it's truly empty or only has a br, and has no other children
      if ((text === '' || hasOnlyBr) && block.children.length === 0 && block.parentNode) {
        const br = document.createElement('br');
        block.parentNode.replaceChild(br, block);
      }
    });
    
    return temp.innerHTML;
  };

  // Initialize content and handle external updates
  useEffect(() => {
    if (editorRef.current && !isTypingRef.current) {
      const currentContent = editorRef.current.innerHTML;
      const isEmpty = !currentContent || currentContent.trim() === '' || currentContent === '<br>';
      
      // Always update if editor is empty or if value is different
      // This ensures content loads correctly when page refreshes
      if (value !== undefined && (isEmpty || value !== currentContent)) {
        // Normalize the content before setting it - but preserve br tags
        const normalizedValue = normalizeContent(value);
        
        // Set the content
        editorRef.current.innerHTML = normalizedValue || '';
      }
    }
  }, [value]);

  const execCommand = useCallback((command, value = null, event = null) => {
    // Prevent any default behavior that might trigger SEO validation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Focus the editor first
    editorRef.current?.focus();
    
    
    // Ensure we have a selection in the editor
    const selection = window.getSelection();
    let range;
    
    if (selection.rangeCount === 0) {
      // Create a range at the end of the editor if no selection exists
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // Collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      range = selection.getRangeAt(0);
    }
    
    // Use modern Selection API instead of deprecated execCommand
    switch (command) {
      case 'bold':
        toggleFormat('strong');
        break;
      case 'italic':
        toggleFormat('em');
        break;
      case 'underline':
        toggleFormat('u');
        break;
      case 'insertUnorderedList':
        insertList('ul');
        break;
      case 'insertOrderedList':
        insertList('ol');
        break;
      case 'justifyLeft':
        setAlignment('left');
        break;
      case 'justifyCenter':
        setAlignment('center');
        break;
      case 'justifyRight':
        setAlignment('right');
        break;
      case 'formatBlock':
        formatBlock(value);
        break;
      default:
        // Fallback to execCommand for other commands
        document.execCommand(command, false, value);
    }
    
    // Update the content
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const toggleFormat = (tag) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    
    
    if (selectedText) {
      // Wrap selected text
      const element = document.createElement(tag);
      element.textContent = selectedText;
      range.deleteContents();
      range.insertNode(element);
    } else {
      // Toggle format for current position
      const element = document.createElement(tag);
      element.textContent = 'text';
      range.insertNode(element);
      range.setStartAfter(element);
      range.setEndAfter(element);
    }
    
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const insertList = (listType) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const list = document.createElement(listType);
    const li = document.createElement('li');
    li.textContent = 'List item';
    list.appendChild(li);
    
    range.insertNode(list);
    range.setStart(li, 0);
    range.setEnd(li, 0);
    
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const setAlignment = (alignment) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const blockElement = container.nodeType === Node.TEXT_NODE 
      ? container.parentElement 
      : container;
    
    if (blockElement) {
      blockElement.style.textAlign = alignment;
    }
  };

  const formatBlock = (blockType) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const element = document.createElement(blockType);
    
    if (blockType === 'blockquote') {
      element.textContent = 'Quote text';
    } else if (blockType === 'pre') {
      element.textContent = 'Code block';
    } else if (blockType.startsWith('h')) {
      // For headings, use selected text or placeholder
      const selectedText = range.toString();
      element.textContent = selectedText || 'Heading text';
    } else if (blockType === 'p') {
      // For paragraphs, use selected text or placeholder
      const selectedText = range.toString();
      element.textContent = selectedText || 'Paragraph text';
    }
    
    range.insertNode(element);
    range.setStart(element, 0);
    range.setEnd(element, 0);
    
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const insertImage = useCallback(async () => {
    if (!imageData.url.trim()) return;

    // Focus the editor first
    editorRef.current?.focus();

    // Get current selection or create one at the end
    const selection = window.getSelection();
    let range;
    
    if (selection.rangeCount === 0) {
      // Create a range at the end of the editor if no selection exists
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // Collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      range = selection.getRangeAt(0);
    }

    // Create the image element
    const img = document.createElement('img');
    img.src = imageData.url;
    img.alt = imageData.alt || '';
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.borderRadius = '8px';
    img.style.margin = '16px 0';
    img.style.display = 'block';

    // Insert the image
    range.insertNode(img);
    
    // Move cursor after the image
    range.setStartAfter(img);
    range.setEndAfter(img);
    selection.removeAllRanges();
    selection.addRange(range);

    // Update content
    onChange(editorRef.current?.innerHTML || '');
    
    setImageData({ url: '', alt: '' });
    setShowImageModal(false);
  }, [imageData, onChange]);

  const insertVideo = useCallback(() => {
    if (!videoData.url.trim()) return;

    // Focus the editor first
    editorRef.current?.focus();

    // Get current selection or create one at the end
    const selection = window.getSelection();
    let range;
    
    if (selection.rangeCount === 0) {
      // Create a range at the end of the editor if no selection exists
      range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false); // Collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      range = selection.getRangeAt(0);
    }

    let embedUrl = videoData.url;
    
    // Convert YouTube URLs to embed format
    if (videoData.url.includes('youtube.com/watch')) {
      const videoId = videoData.url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    } else if (videoData.url.includes('youtu.be/')) {
      const videoId = videoData.url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      }
    } else if (videoData.url.includes('vimeo.com/')) {
      const videoId = videoData.url.split('vimeo.com/')[1]?.split('?')[0];
      if (videoId) {
        embedUrl = `https://player.vimeo.com/video/${videoId}`;
      }
    }

    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.width = '100%';
    iframe.height = '315';
    iframe.frameBorder = '0';
    iframe.allowFullscreen = true;
    iframe.style.borderRadius = '8px';
    iframe.style.margin = '16px 0';

    // Insert the iframe
    range.insertNode(iframe);
    
    // Move cursor after the iframe
    range.setStartAfter(iframe);
    range.setEndAfter(iframe);
    selection.removeAllRanges();
    selection.addRange(range);

    // Update content
    onChange(editorRef.current?.innerHTML || '');
    
    setVideoData({ url: '', title: '' });
    setShowVideoModal(false);
  }, [videoData, onChange]);

  const insertTable = useCallback((rows = 3, cols = 3) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    
    // Create table element
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.margin = '16px 0';
    table.style.borderRadius = '8px';
    table.style.overflow = 'hidden';
    table.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
    table.style.background = 'rgba(255, 255, 255, 0.05)';
    
    // Create table header
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    headerRow.style.background = 'rgba(255, 107, 53, 0.2)';
    
    for (let i = 0; i < cols; i++) {
      const th = document.createElement('th');
      th.style.padding = '12px';
      th.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
      th.style.color = '#ffffff';
      th.style.fontWeight = '600';
      th.style.textAlign = 'left';
      th.contentEditable = true;
      th.textContent = `Header ${i + 1}`;
      headerRow.appendChild(th);
    }
    
    thead.appendChild(headerRow);
    table.appendChild(thead);
    
    // Create table body
    const tbody = document.createElement('tbody');
    
    for (let i = 0; i < rows; i++) {
      const row = document.createElement('tr');
      row.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
      
      // Add hover effect
      row.addEventListener('mouseenter', () => {
        row.style.background = 'rgba(255, 255, 255, 0.05)';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = 'transparent';
      });
      
      for (let j = 0; j < cols; j++) {
        const td = document.createElement('td');
        td.style.padding = '12px';
        td.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
        td.style.color = '#cccccc';
        td.contentEditable = true;
        td.textContent = `Cell ${i + 1},${j + 1}`;
        row.appendChild(td);
      }
      
      tbody.appendChild(row);
    }
    
    table.appendChild(tbody);
    
    // Insert the table
    range.insertNode(table);
    
    // Move cursor to first cell
    const firstCell = table.querySelector('th, td');
    if (firstCell) {
      const newRange = document.createRange();
      newRange.selectNodeContents(firstCell);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    // Update content
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      console.log('Uploading file:', file.name, 'Size:', file.size, 'Type:', file.type);

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        body: formData,
      });

      console.log('Upload response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Upload successful:', data);
        setImageData({ url: data.url, alt: '' });
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Upload failed:', errorData);
        alert(`Failed to upload image: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Failed to upload image: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        handleFileUpload(file);
        break;
      }
    }
  }, [handleFileUpload]);

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarButton
          onClick={(e) => execCommand('bold', null, e)}
          title="Bold"
        >
          <Bold size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('italic', null, e)}
          title="Italic"
        >
          <Italic size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('underline', null, e)}
          title="Underline"
        >
          <Underline size={16} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={(e) => execCommand('formatBlock', 'h1', e)}
          title="Heading 1"
        >
          <Heading1 size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('formatBlock', 'h2', e)}
          title="Heading 2"
        >
          <Heading2 size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('formatBlock', 'h3', e)}
          title="Heading 3"
        >
          <Heading3 size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('formatBlock', 'h4', e)}
          title="Heading 4"
        >
          <Heading4 size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('formatBlock', 'p', e)}
          title="Paragraph"
        >
          <Type size={16} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={(e) => execCommand('insertUnorderedList', null, e)}
          title="Bullet List"
        >
          <List size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('insertOrderedList', null, e)}
          title="Numbered List"
        >
          <ListOrdered size={16} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={(e) => execCommand('justifyLeft', null, e)}
          title="Align Left"
        >
          <AlignLeft size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('justifyCenter', null, e)}
          title="Align Center"
        >
          <AlignCenter size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('justifyRight', null, e)}
          title="Align Right"
        >
          <AlignRight size={16} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={(e) => execCommand('formatBlock', 'blockquote', e)}
          title="Quote"
        >
          <Quote size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => execCommand('formatBlock', 'pre', e)}
          title="Code Block"
        >
          <Code size={16} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowTableModal(true);
          }}
          title="Insert Table"
        >
          <Table size={16} />
        </ToolbarButton>

        <ToolbarSeparator />

        <ToolbarButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowImageModal(true);
          }}
          title="Insert Image"
        >
          <Image size={16} />
        </ToolbarButton>
        
        <ToolbarButton
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowVideoModal(true);
          }}
          title="Insert Video"
        >
          <Video size={16} />
        </ToolbarButton>
      </Toolbar>

      <EditorContent
        ref={editorRef}
        contentEditable
        onKeyDown={(e) => {
          // Mark that user is actively typing
          isTypingRef.current = true;
          
          // Handle Enter key to create proper line breaks
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            
            const selection = window.getSelection();
            if (selection.rangeCount === 0) return;
            
            const range = selection.getRangeAt(0);
            
            // Delete any selected content
            range.deleteContents();
            
            // Insert a <br> tag
            const br = document.createElement('br');
            range.insertNode(br);
            
            // Move cursor after the <br>
            range.setStartAfter(br);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            
            // Manually trigger onChange to update the value immediately
            const newContent = e.target.innerHTML;
            onChange(newContent);
          } else if (e.key === 'Enter' && e.shiftKey) {
            // Shift+Enter also creates a <br> tag
            e.preventDefault();
            e.stopPropagation();
            
            const selection = window.getSelection();
            if (selection.rangeCount === 0) return;
            
            const range = selection.getRangeAt(0);
            range.deleteContents();
            
            const br = document.createElement('br');
            range.insertNode(br);
            
            range.setStartAfter(br);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            
            const newContent = e.target.innerHTML;
            onChange(newContent);
          }
        }}
        onInput={(e) => {
          // Update content without interfering with cursor
          let newContent = e.target.innerHTML;
          
          // Aggressively convert div/p tags to br tags - this is critical for preserving line breaks
          if (newContent.includes('<div') || newContent.includes('<p')) {
            const temp = document.createElement('div');
            temp.innerHTML = newContent;
            
            // Replace all div and p tags with br tags
            // First, handle empty divs/p tags
            const emptyBlocks = temp.querySelectorAll('div:empty, p:empty');
            emptyBlocks.forEach(block => {
              const br = document.createElement('br');
              if (block.parentNode) {
                block.parentNode.replaceChild(br, block);
              }
            });
            
            // Then handle divs/p tags that only contain text (no other block elements)
            const textBlocks = temp.querySelectorAll('div, p');
            textBlocks.forEach(block => {
              // Skip if it has other block elements inside
              if (block.querySelector('div, p, h1, h2, h3, h4, h5, h6, ul, ol')) {
                return;
              }
              
              // Get the text content
              const text = block.textContent || '';
              const innerHTML = block.innerHTML.trim();
              
              // If it's just text or text with inline elements, convert to br + text
              if (text.trim() || innerHTML) {
                // Create a document fragment with the content
                const fragment = document.createDocumentFragment();
                
                // If there's text before, add it
                if (text.trim()) {
                  const textNode = document.createTextNode(text);
                  fragment.appendChild(textNode);
                }
                
                // Add a br tag
                const br = document.createElement('br');
                fragment.appendChild(br);
                
                // Replace the div/p with the fragment
                if (block.parentNode) {
                  block.parentNode.replaceChild(fragment, block);
                }
              } else {
                // Empty or whitespace only, just replace with br
                const br = document.createElement('br');
                if (block.parentNode) {
                  block.parentNode.replaceChild(br, block);
                }
              }
            });
            
            newContent = temp.innerHTML;
            
            // Clean up any remaining empty divs/p tags
            newContent = newContent.replace(/<div[^>]*>\s*<\/div>/gi, '<br>');
            newContent = newContent.replace(/<p[^>]*>\s*<\/p>/gi, '<br>');
            newContent = newContent.replace(/<div[^>]*><br><\/div>/gi, '<br>');
            newContent = newContent.replace(/<p[^>]*><br><\/p>/gi, '<br>');
          }
          
          // RichTextEditor onInput: Calling onChange
          onChange(newContent);
          
          // Reset typing flag after a short delay
          setTimeout(() => {
            isTypingRef.current = false;
          }, 100);
        }}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {showImageModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowImageModal(false)}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Insert Image</ModalTitle>
              <CloseButton onClick={() => setShowImageModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <FileUploadArea
              onClick={() => document.getElementById('image-upload').click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <Upload size={32} style={{ color: '#6c757d', marginBottom: '8px' }} />
              <UploadText>Click to upload or drag and drop</UploadText>
              <UploadSubtext>PNG, JPG, GIF up to 10MB</UploadSubtext>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileUpload(e.target.files[0])}
              />
            </FileUploadArea>

            {imageData.url && (
              <PreviewImage src={imageData.url} alt="Preview" />
            )}

            <FormGroup>
              <Label>Image URL</Label>
              <Input
                type="url"
                value={imageData.url}
                onChange={(e) => setImageData({ ...imageData, url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </FormGroup>

            <FormGroup>
              <Label>Alt Text</Label>
              <Input
                type="text"
                value={imageData.alt}
                onChange={(e) => setImageData({ ...imageData, alt: e.target.value })}
                placeholder="Describe the image for accessibility"
              />
            </FormGroup>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ButtonSecondary onClick={() => setShowImageModal(false)}>
                Cancel
              </ButtonSecondary>
              <Button 
                onClick={insertImage}
                disabled={!imageData.url.trim() || uploading}
              >
                {uploading ? 'Uploading...' : 'Insert Image'}
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {showVideoModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowVideoModal(false)}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Insert Video</ModalTitle>
              <CloseButton onClick={() => setShowVideoModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <FormGroup>
              <Label>Video URL</Label>
              <Input
                type="url"
                value={videoData.url}
                onChange={(e) => setVideoData({ ...videoData, url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Video Title (Optional)</Label>
              <Input
                type="text"
                value={videoData.title}
                onChange={(e) => setVideoData({ ...videoData, title: e.target.value })}
                placeholder="Video title for accessibility"
              />
            </FormGroup>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ButtonSecondary onClick={() => setShowVideoModal(false)}>
                Cancel
              </ButtonSecondary>
              <Button 
                onClick={insertVideo}
                disabled={!videoData.url.trim()}
              >
                Insert Video
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {showTableModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowTableModal(false)}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Insert Table</ModalTitle>
              <CloseButton onClick={() => setShowTableModal(false)}>
                <X size={20} />
              </CloseButton>
            </ModalHeader>

            <FormGroup>
              <Label>Number of Rows</Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={tableData.rows}
                onChange={(e) => setTableData({ ...tableData, rows: parseInt(e.target.value) || 1 })}
                placeholder="3"
              />
            </FormGroup>

            <FormGroup>
              <Label>Number of Columns</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={tableData.cols}
                onChange={(e) => setTableData({ ...tableData, cols: parseInt(e.target.value) || 1 })}
                placeholder="3"
              />
            </FormGroup>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ButtonSecondary onClick={() => setShowTableModal(false)}>
                Cancel
              </ButtonSecondary>
              <Button 
                onClick={() => {
                  insertTable(tableData.rows, tableData.cols);
                  setShowTableModal(false);
                }}
                disabled={tableData.rows < 1 || tableData.cols < 1}
              >
                Insert Table
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </EditorContainer>
  );
};

export default RichTextEditor;
